import _ from "lodash";
import React from "react";
import GameBoard from "../components/GameBoard";
import KenoBonusLogo from "../components/KenoBonusLogo";
import KenoLogo from "../components/KenoLogo";
import PrizeTable from "../components/PrizeTable";
import {
    BASE_10,
    DOLLAR_IN_CENTS,
    PURCHASE_INSUFFICIENT_FUNDS,
    SERVER_ERROR,
} from "../constants";
import ICustomer from "../interfaces/ICustomer";
import IError from "../interfaces/IError";
import IEvent from "../interfaces/IEvent";
import IKenoDraw from "../interfaces/IKenoDraw";
import IOutcome from "../interfaces/IOutcome";
import IProductPlayType from "../interfaces/IProductPlayType";
import IProductPlayTypeComponent from "../interfaces/IProductPlayTypeComponent";
import IPurchaseTicketData from "../interfaces/IPurchaseTicketData";
import ITicket from "../interfaces/ITicket";
import {
    BONUS_COST_MULTIPLIER,
    BONUS_OUTCOME_NAME,
    BONUS_OUTCOME_TYPE_NAME,
    MAX_NUMBER,
    MIN_NUMBER,
    PRODUCTPLAYTYPE_NAME,
    PURCHASE_CONFIRM_SKIP,
} from "../settings";
import formatCountdown from "../utils/formatCountdown";
import formatOgsDollar from "../utils/formatOgsDollar";
import getQueryParameters from "../utils/getQueryParameters";
import history from "../utils/history";
import OGSAPI from "../utils/OGSAPI";

const TRANSLATIONS: {
    [key: string]: string | undefined;
} = {
    [PURCHASE_INSUFFICIENT_FUNDS]: "You do not have enough funds to purchase your ticket.",
    [SERVER_ERROR]: "There was a general server error.",
};

interface IProps {
    customer?: ICustomer;
    nextDraw: IKenoDraw;
    productPlayType: IProductPlayType;
    productPlayTypeComponents: IProductPlayTypeComponent[];
}

interface IState {
    bonusSelectable: boolean;
    event?: IEvent;
    maxSelectionCount: number;
    minSelectionCount: number;
    outcomes: IOutcome[];
    outcomesByExternalId: { [key: string]: IOutcome | undefined };
    purchaseData: {
        bonus: boolean;
        draws: number;
        quickPick: boolean;
        wager: number;
    };
    purchaseError?: IError;
    purchasing: boolean;
    quickPickSelectionCount: number;
    selectedNumbers: number[];
    state: "select" | "confirm";
}

interface IQueryString {
    bonus?: string;
    draws?: string;
    numbers?: string;
    wager?: string;
}

export default class PurchasePage extends React.Component<IProps, IState> {
    public state: IState = {
        bonusSelectable: false,
        maxSelectionCount: 0,
        minSelectionCount: 0,
        outcomes: [],
        outcomesByExternalId: {},
        purchaseData: {
            bonus: false,
            draws: 1,
            quickPick: false,
            wager: 0,
        },
        purchasing: false,
        quickPickSelectionCount: 1,
        selectedNumbers: [],
        state: "select",
    };
    public constructor(props: IProps) {
        super(props);

        const { productPlayType } = props;
        const { settings } = productPlayType;
        const { ticketCosts } = settings;

        this.state.purchaseData.wager = ticketCosts[0];
    }
    public componentDidMount(): void {

        const queryString = getQueryParameters() as IQueryString;

        if (queryString) {
            const {
                productPlayType,
            } = this.props;
            const { settings } = productPlayType;
            const { ticketCosts } = settings;

            const bonus = queryString.bonus === "1";

            let draws = queryString.draws ? parseInt(queryString.draws, BASE_10) : 1;
            draws = this.cleanDrawsQuantity(draws);

            let wager = queryString.wager ? parseInt(queryString.wager, BASE_10) : ticketCosts[0];

            if (!ticketCosts.includes(wager)) {
                wager = ticketCosts[0];
            }

            const selectedNumbers: number[] = [];
            if (queryString.numbers) {
                for (const n of _.uniq(queryString.numbers.split(","))) {
                    const num = parseInt(n, BASE_10);
                    if (isNaN(num) || num < MIN_NUMBER || num > MAX_NUMBER) {
                        continue;
                    }
                    selectedNumbers.push(num);
                }
            }

            this.setState({
                purchaseData: {
                    ...this.state.purchaseData,
                    bonus,
                    draws,
                    wager,
                },
                selectedNumbers,
            });
        }

        const { nextDraw } = this.props;
        if (nextDraw) {
            this.fetchDrawEvent(nextDraw.id);
        }
    }
    public componentWillReceiveProps(nextProps: IProps): void {
        if (nextProps.nextDraw && nextProps.nextDraw !== this.props.nextDraw) {
            this.fetchDrawEvent(nextProps.nextDraw.id);
        }

        // Compute min/max selection counts
        const componentsByName = this.getComponentsByOutcomeTypeName();

        let minSelectionCount = 0;
        let maxSelectionCount = 0;
        const numbersComponent = componentsByName.Number;
        if (numbersComponent) {
            minSelectionCount = numbersComponent.min;
            maxSelectionCount = numbersComponent.max;
        }

        const bonusComponent = componentsByName[BONUS_OUTCOME_TYPE_NAME];
        const bonusSelectable = !!(bonusComponent && bonusComponent.max > 0);

        this.setState({
            bonusSelectable,
            maxSelectionCount,
            minSelectionCount,
        });
    }
    public fetchDrawEvent(id: string): Promise<IEvent[]> {
        this.setState({
            event: undefined,
            outcomes: [],
        });

        const promise = OGSAPI.fetchEvents({
            external_id: id,
            productplaytype__name: "Keno",
        });

        promise.then((events: IEvent[]): void => {
            const event = events[0];
            this.setState({
                event,
            });

            OGSAPI.fetchOutcomes({
                productId: event.productId,
                relatedCollection: event.externalId,
            }).then((outcomes: IOutcome[]) => {
                const outcomesByExternalId: { [key: string]: IOutcome } = {};

                outcomes.forEach((outcome: IOutcome): void => {
                    if (outcome.externalId) {
                        outcomesByExternalId[outcome.externalId] = outcome;
                    }
                });

                this.setState({
                    outcomes,
                    outcomesByExternalId,
                });
            });
        });

        return promise;
    }
    public render(): JSX.Element {
        const {
            nextDraw,
            productPlayType,
        } = this.props;
        const {
            bonusSelectable,
            selectedNumbers,
            quickPickSelectionCount,
            state,
            purchaseData,
            purchaseError,
            purchasing,
        } = this.state;

        const { wager, draws, bonus, quickPick } = purchaseData;

        const totalCost = this.getTotalCost();

        const [minSelectionCount, maxSelectionCount] = this.getSelectionCountMinMax();
        const purchasable = selectedNumbers.length >= minSelectionCount &&
            selectedNumbers.length <= maxSelectionCount &&
            totalCost > 0;

        let countdown = "-";
        if (nextDraw && nextDraw.scheduledDrawTimestamp) {
            countdown = formatCountdown(nextDraw.scheduledDrawTimestamp, {
                zero: "0:00",
            });
        }

        const { settings } = productPlayType;
        const { prizeTable, ticketCosts, maxAdvanceBuy } = settings;

        const ALL_SIDEBAR_COLUMNS = 2;

        return (
            <div className="purchase-page columns is-gapless">
                <div className="sidebar column is-4">
                    <div className="sidebar-wrapper">
                        <div className="sidebar-header">Purchase Ticket</div>
                        <div className="sidebar-content">
                            <div className="purchase-sidebar">
                                <table className="row-form">
                                    <tbody>
                                        { state === "select" ? (
                                            <tr className="field">
                                                <th>Quick Pick:</th>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={ quickPick }
                                                        onChange={ (e: React.ChangeEvent<HTMLInputElement>): void => {
                                                            this.setQuickPickState(e.target.checked);
                                                        } }
                                                    />
                                                </td>
                                            </tr>
                                        ) : undefined }
                                        { state === "confirm" ? (
                                            <tr className="field">
                                                <th>Ticket Type:</th>
                                                <td>{ quickPick ? "Quick Pick" : "Player Selected" }</td>
                                            </tr>
                                        ) : undefined }
                                        { quickPick ? (
                                            <tr className="field">
                                                <th>Picks Per Draw:</th>
                                                { state === "confirm" ? (
                                                    <td>{ quickPickSelectionCount }</td>
                                                ) : (
                                                    <td>
                                                        <div className="select">
                                                            <select
                                                                value={ String(quickPickSelectionCount) }
                                                                onChange={ (
                                                                    e: React.ChangeEvent<HTMLSelectElement>,
                                                                ): void => {
                                                                        let num = parseInt(e.target.value, BASE_10);
                                                                        if (isNaN(num)) {
                                                                            num = 1;
                                                                        }
                                                                        this.setState({
                                                                            quickPickSelectionCount: num,
                                                                            selectedNumbers:
                                                                                this.getQuickPickNumbers(num),
                                                                        });
                                                                    }
                                                                }
                                                            >
                                                                {
                                                                    _.range(minSelectionCount, maxSelectionCount + 1)
                                                                    .map((num: number): JSX.Element => {
                                                                        return (
                                                                            <option
                                                                                key={ num }
                                                                                value={ num }
                                                                            >{ num }</option>
                                                                        );
                                                                    })
                                                                }
                                                            </select>
                                                        </div>
                                                    </td>
                                                ) }
                                            </tr>
                                        ) : undefined }
                                        <tr className="field">
                                            <th>Number of Draws:</th>
                                            { state === "confirm" ? (
                                                <td>{ draws }</td>
                                            ) : (
                                                <td>
                                                    <div className="select">
                                                        <select
                                                            value={ draws }
                                                            onChange={ (
                                                                e: React.ChangeEvent<HTMLSelectElement>,
                                                            ): void => {
                                                                this.setState({
                                                                    purchaseData: {
                                                                        ...purchaseData,
                                                                        draws: parseInt(e.target.value, BASE_10),
                                                                    },
                                                                });
                                                            } }
                                                        >
                                                            { _.range(1, maxAdvanceBuy + 1).map((
                                                                num: number,
                                                            ): JSX.Element => {
                                                                return (
                                                                    <option
                                                                        key={ num }
                                                                        value={ num }
                                                                    >{ num }</option>
                                                                );
                                                            }) }
                                                        </select>
                                                    </div>
                                                </td>
                                            ) }
                                        </tr>
                                        <tr className="field">
                                            <th>Wager Per Draw:</th>
                                            { state === "select" ? (
                                                <td>
                                                    <div className="select">
                                                        <select
                                                            value={ wager }
                                                            onChange={ (
                                                                e: React.ChangeEvent<HTMLSelectElement>,
                                                            ): void => {
                                                                this.setState({
                                                                    purchaseData: {
                                                                        ...this.state.purchaseData,
                                                                        wager: parseInt(e.target.value, BASE_10),
                                                                    },
                                                                });
                                                            } }
                                                        >
                                                            { ticketCosts.map((amount: number, index: number) => {
                                                                return (
                                                                    <option key={ index } value={ amount }>
                                                                        { formatOgsDollar(amount) }
                                                                    </option>
                                                                );
                                                            }) }
                                                        </select>
                                                    </div>
                                                </td>
                                            ) : (
                                                <td>
                                                    { formatOgsDollar(wager) }
                                                </td>
                                            ) }
                                        </tr>
                                        { bonusSelectable ? (
                                            <tr className="field last">
                                                <th>
                                                    <KenoBonusLogo />
                                                </th>
                                                { state === "select" ? (
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={ bonus }
                                                            onChange={
                                                                (e: React.ChangeEvent<HTMLInputElement>): void => {
                                                                    this.setState({
                                                                        purchaseData: {
                                                                            ...this.state.purchaseData,
                                                                            bonus: e.target.checked,
                                                                        },
                                                                    });
                                                                } }
                                                        />
                                                    </td>
                                                ) : (
                                                    <td>
                                                        { bonus ? "YES" : "NO" }
                                                    </td>
                                                ) }
                                            </tr>
                                        ) : undefined }
                                        <tr>
                                            <th>Total:</th>
                                            <td>{ formatOgsDollar(totalCost) }</td>
                                        </tr>
                                        { state === "select" ? (
                                            <>
                                                <tr>
                                                    <td colSpan={ ALL_SIDEBAR_COLUMNS }>
                                                        <button
                                                            onClick={ (): void => {
                                                                if (PURCHASE_CONFIRM_SKIP) {
                                                                    this.handlePurchaseClick();
                                                                } else {
                                                                    this.setState({ state: "confirm" });
                                                                }
                                                            } }
                                                            disabled={ !purchasable }
                                                            type="button"
                                                            className="styled-button"
                                                        >{ PURCHASE_CONFIRM_SKIP ? "Buy Now" : "Continue" }</button>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan={ ALL_SIDEBAR_COLUMNS }>
                                                        <button
                                                            type="button"
                                                            onClick={ (): void => { history.push("/"); } }
                                                            className="link-button"
                                                        >Cancel</button>
                                                    </td>
                                                </tr>
                                            </>
                                        ) : (
                                            <>
                                                { purchaseError ? (
                                                    <tr>
                                                        <td colSpan={ ALL_SIDEBAR_COLUMNS }>
                                                            <div className="field help is-danger has-text-centered">
                                                                { TRANSLATIONS[purchaseError.type] }
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : undefined }
                                                <tr>
                                                    <td colSpan={ ALL_SIDEBAR_COLUMNS }>
                                                        <button
                                                            type="button"
                                                            className="styled-button"
                                                            onClick={ this.handlePurchaseClick.bind(this) }
                                                            disabled={ purchasing }
                                                        >Buy Now</button>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan={ ALL_SIDEBAR_COLUMNS }>
                                                        <button
                                                            type="button"
                                                            onClick={ (): void => {
                                                                this.setState({ state: "select" });
                                                            } }
                                                            className="link-button"
                                                        >Modify Ticket</button>
                                                    </td>
                                                </tr>
                                            </>
                                        ) }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    { selectedNumbers.length === 0 ? (
                        undefined
                    ) : (
                        <div className="sidebar-wrapper">
                            <PrizeTable
                                prizeTable={ prizeTable }
                                selectionCount={ selectedNumbers.length }
                                multiplier={ wager / DOLLAR_IN_CENTS }
                            />
                        </div>
                    ) }
                </div>
                <div className="play-area column is-8">
                    <div className="play-area-wrapper">
                        <div className="play-area-header">Your Numbers</div>
                        <GameBoard
                            mode={ quickPick ? "confirm" : state }
                            nextDraw={ nextDraw }
                            selectedNumbers={ selectedNumbers }
                            onSelectedNumbersChange={ this.handleSelectedNumbersChange.bind(this) }
                            maxSelections={ maxSelectionCount }
                        >
                            <div className="columns">
                                <div className="column">
                                    <KenoLogo />
                                </div>
                                <div className="column has-text-centered pick-your-numbers-text">
                                    Pick Your Numbers
                                    </div>
                                <div className="column">
                                    <label>Next Draw</label>
                                    <var>{ countdown }</var>
                                </div>
                            </div>
                        </GameBoard>
                    </div>
                </div>
            </div>
        );
    }
    protected cleanDrawsQuantity(draws: number): number {
        const { settings } = this.props.productPlayType;
        const { maxAdvanceBuy } = settings;

        if (draws < 1) {
            return 1;
        } else if (draws > maxAdvanceBuy) {
            return maxAdvanceBuy;
        }
        return draws;
    }
    protected getComponentsByOutcomeTypeName(): { [key: string]: undefined | IProductPlayTypeComponent } {
        const { productPlayTypeComponents } = this.props;
        const componentsByName: { [key: string]: undefined | IProductPlayTypeComponent } = {};
        productPlayTypeComponents.forEach((component: IProductPlayTypeComponent): void => {
            componentsByName[component.outcomeType.name] = component;
        });
        return componentsByName;
    }
    protected getQuickPickNumbers(providedQuantity?: number): number[] {
        const range = _.range(1, MAX_NUMBER);

        let quantity = providedQuantity;
        if (!quantity) {
            if (this.state) {
                const { quickPickSelectionCount } = this.state;
                quantity = quickPickSelectionCount;
            } else {
                quantity = 1;
            }
        }

        return _.sampleSize(range, quantity);
    }
    protected getSelectionCountMinMax(): [number, number] {
        const { minSelectionCount, maxSelectionCount } = this.state;
        return [minSelectionCount, maxSelectionCount];
    }
    protected getTotalCost(): number {
        const { wager, draws, bonus } = this.state.purchaseData;
        const totalCost = wager * draws * (bonus ? BONUS_COST_MULTIPLIER : 1);
        return totalCost;
    }
    protected handlePurchaseClick(): void {
        const { customer } = this.props;

        if (customer) {
            this.purchaseTicket();
        } else {
            this.redirectToLogin();
        }
    }
    protected handlePurchaseError(): void {
        this.setState({
            purchaseError: {
                type: SERVER_ERROR,
            },
        });
    }
    protected handleSelectedNumbersChange(selectedNumbers: number[]): void {
        this.setState({
            selectedNumbers,
        });
    }
    protected async processPurchases(datas: IPurchaseTicketData[]): Promise<ITicket[]> {
        const results: ITicket[] = [];
        for (const data of datas) {
            const ticket = await OGSAPI.purchaseTicket(data);
            results.push(ticket);
        }
        return results;
    }
    protected purchaseTicket(): void {
        const { selectedNumbers, purchaseData } = this.state;

        const { bonus, draws, wager } = purchaseData;
        const DOUBLE_BONUS_PRICE = 2;
        const cost = wager * (bonus ? DOUBLE_BONUS_PRICE : 1);

        const purchaseTicketDatas: IPurchaseTicketData[] = [];

        this.setState({
            purchasing: true,
        });

        // FIXME: Improve this logic to make use of a bulk purchase type of API
        OGSAPI.fetchKenoDraws({
            limit: draws,
            order_by: "id",
            status: "open",
        }).then((fetchedDraws: IKenoDraw[]): void => {
            if (fetchedDraws.length < draws) {
                throw new Error(
                    `Not enough draws to purchase from: ${ fetchedDraws.length },
                    expected: ${ draws }`);
            }
            const drawIds: string[] = fetchedDraws.map((draw: IKenoDraw): string => {
                return draw.id;
            });

            OGSAPI.fetchEvents({
                external_id__in: drawIds.join(","),
                order_by: "external_id",
                productplaytype__name: PRODUCTPLAYTYPE_NAME,
            }).then((events: IEvent[]): void => {
                if (events.length < draws) {
                    throw new Error(
                        `Not enough events to purchase from: ${ events.length },
                        expected: ${ draws }`);
                }

                events.forEach((event: IEvent): void => {
                    OGSAPI.fetchOutcomes({
                        productId: event.productId,
                        relatedCollection: event.externalId,
                    }).then((outcomes: IOutcome[]): void => {
                        const EXPECTED_OUTCOME_COUNT = 81;
                        if (outcomes.length !== EXPECTED_OUTCOME_COUNT) {
                            throw new Error(
                                `Unexpected number of outcomes: ${ outcomes.length },
                                expected: ${ EXPECTED_OUTCOME_COUNT }.`);
                        }

                        // Index the Outcomes
                        const outcomesByNumber: { [key: string]: IOutcome } = {};
                        let bonusOutcome: IOutcome | undefined;
                        outcomes.forEach((outcome: IOutcome): void => {
                            if (outcome.name === BONUS_OUTCOME_NAME) {
                                bonusOutcome = outcome;
                            } else if (outcome.externalId) {
                                outcomesByNumber[outcome.externalId] = outcome;
                            }
                        });
                        if (!bonusOutcome) {
                            throw new Error("Bonus outcome not found.");
                        }

                        // Build the list of selections for purchase
                        interface IPurchaseSelection {
                            outcomeId: string;
                        }
                        const selections: IPurchaseSelection[] = [];
                        const eventId = event.id;

                        selectedNumbers.forEach((num: number): void => {
                            const outcome = outcomesByNumber[num];
                            if (!outcome) {
                                throw new Error(`Unable to find outcome for ${ num }.`);
                            }
                            selections.push({
                                outcomeId: outcome.id,
                            });
                        });

                        if (bonus && bonusOutcome) {
                            selections.push({
                                outcomeId: bonusOutcome.id,
                            });
                        }

                        const purchaseTicketData: IPurchaseTicketData = {
                            cost,
                            eventId,
                            selections,
                            wager,
                        };

                        purchaseTicketDatas.push(purchaseTicketData);

                        // Purchase tickets sequentially
                        if (purchaseTicketDatas.length === draws) {
                            this.processPurchases(purchaseTicketDatas).then((): void => {
                                history.push("/draw/");
                                window.parent.postMessage("purchaseCompleted", "*");
                            }, (errors: Array<IError | undefined>): void => {
                                let purchaseError = errors[0];
                                if (!purchaseError) {
                                    purchaseError = {
                                        type: SERVER_ERROR,
                                    };
                                }
                                this.setState({
                                    purchaseError,
                                    purchasing: false,
                                });
                            });
                        }
                    }, this.handlePurchaseError);
                });
            }, this.handlePurchaseError);
        }, this.handlePurchaseError);
    }
    protected redirectToLogin(): void {
        window.parent.postMessage("redirectToLogin", "*");
    }
    protected setQuickPickState(quickPick: boolean): void {
        const { purchaseData } = this.state;

        this.setState({
            purchaseData: {
                ...purchaseData,
                quickPick,
            },
            quickPickSelectionCount: 1,
            selectedNumbers: quickPick ? this.getQuickPickNumbers(1) : [],
        });
    }
}



// WEBPACK FOOTER //
// ./src/pages/PurchasePage.tsx